// === WOONHUIS — Infofolio value mappers ===
// Pure mappers extracted verbatim from WoonhuisScreen: they translate Infofolio's free-text /
// coded property values into the AFD codes the Woonhuis form uses. Unknown values map to null
// so the field stays empty for the user to fill.

import { INBOEDEL_ONLY_HOUSE_TYPES, houseTypeOptionsFor } from '../../../utils/houseType';

const WOONHUIS_HOUSE_TYPE_CODES = houseTypeOptionsFor('woonhuis').map(([code]) => code);

// Mapping functions for Infofolio text values to form codes
// If value is unknown, return null to leave field empty for user to fill
export const mapTypeWoning = (value: string | undefined): string | null => {
  if (!value) return null;
  // Already a Woonhuis code (utils/houseType minus the inboedel-only ones)?
  if (WOONHUIS_HOUSE_TYPE_CODES.includes(value)) return value;
  // Inboedel-only codes ('A' appartement, '6' maisonnette) become 'U' (Woonhuis treats
  // apartments as tussenwoning).
  if (INBOEDEL_ONLY_HOUSE_TYPES.includes(value)) return 'U';
  // Map known residential text values to codes.
  // Apartment-style text values (Portiekwoning, Galerijwoning, Maisonnette, ...) are intentionally
  // NOT mapped: they don't have a clean Woonhuis code, so leaving the field empty lets the user pick.
  const mappings: Record<string, string> = {
    'eengezinswoning': '1', 'eengezins': '1',
    'vrijstaand': '2', 'vrijstaande woning': '2', 'detached': '2',
    'bejaardenwoning': '3', 'seniorenwoning': '3',
    'herenhuis': '4',
    'geschakelde woning': '10', 'geschakeld': '10',
    'kangoeroewoning': '12', 'mantelzorgwoning': '12',
    'tiny house': '16',
    'containerwoning': '17',
    'grachtenhuis': 'D', 'grachtenpand': 'D',
    'garage': 'G',
    'hoekwoning': 'H', 'corner': 'H', 'hoek': 'H',
    'bungalow': 'L',
    'recreatiewoning': 'R', 'vakantiehuis': 'R', 'vakantiewoning': 'R',
    'twee onder een kap': 'T', '2-onder-1-kap': 'T', '2 onder 1 kap': 'T',
    '2 onder 1 kap woning': 'T', 'twee-onder-een-kap': 'T', 'semi-detached': 'T',
    'tussenwoning': 'U', 'rijtjeshuis': 'U', 'terraced': 'U', 'rijwoning': 'U', 'tussen/rijwoning': 'U',
    'woonboerderij': 'V', 'boerderij': 'V',
    'villa': 'X',
    'chalet': 'Y',
  };
  const lowerValue = value.toLowerCase();
  // Return null for unknown values (e.g., 'Kantoor', 'Bedrijf') - let user select
  return mappings[lowerValue] || null;
};

export const mapAardBouw = (value: string | undefined): string | null => {
  if (!value) return null;
  // Check if it's already a valid code
  const validWallCodes = ['T', 'Y', 'S', 'HA', '3', '4', '5', '6', '7', '8', '9', 'A', 'B', 'BG', 'C', 'D', 'D01', 'D02', 'D03', 'D04', 'D05', 'E', 'F', 'G', 'G01', 'G02', 'G03', 'GP', 'H', 'I', 'J', 'K', 'KR', 'L', 'M', 'N', 'O', 'P', 'PW', 'Q', 'R', 'S01', 'U', 'V', 'W', 'W01', 'X', 'Z'];
  if (validWallCodes.includes(value)) return value;
  // Map text values to codes
  const mappings: Record<string, string> = {
    'staal': 'E', 'steel': 'E',
    'overwegend steen': 'T', 'mostly stone': 'T', 'grotendeels steen': 'T',
    'steen met hout': 'U', 'stone with wood': 'U', 'steen met hout beklampt': 'U',
    'steen': 'Y', 'geheel steen': 'Y', 'full stone': 'Y', 'stone': 'Y', 'steen geheel': 'Y',
    'beton': 'S', 'concrete': 'S', 'grotendeels beton': 'S',
    'hout': 'H', 'wood': 'H', 'grotendeels hout': 'H',
    'metaal': 'B', 'metal': 'B',
    'aluminium': 'X',
    'kunststof': 'D', 'plastic': 'D',
    'houtskelet': 'M', 'wood frame': 'M',
  };
  const lowerValue = value.toLowerCase();
  // Return null for unknown values - let user select
  return mappings[lowerValue] || null;
};

/**
 * Map roof type from Infofolio to AFD BA_AARDDAK codes
 * AFD codes for Woonhuis: H = Hard (pannen/bitumen/etc), K = Kunststof riet
 * Note: Woonhuis doesn't support 'R' (regular riet) - maps to 'K' instead
 *
 * Infofolio provides:
 * - dakbedekking: roof covering material (pannen, bitumen, riet, etc.)
 * - dakconstructie: roof structure (Schuin=sloped, Plat=flat, etc.)
 *
 * Logic:
 * 1. If dakbedekking is available, use it (most specific)
 * 2. If only dakconstructie is available, infer from structure type
 * 3. Sloped roofs (Schuin) typically have hard covering (tiles)
 * 4. Flat roofs (Plat) typically have bitumen (also hard)
 */
export const mapAardDak = (
  value: string | undefined,
  dakconstructie?: string | null,
  dakbedekking?: string | null,
  dakbedekkingCode?: string | null
): string | null => {
  // Check if it's already a valid code
  const validRoofCodes = ['P', 'J', 'H', 'A', 'B', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '2A', '2B', 'C', 'D', 'E', 'F', 'G', 'I', 'K', 'L', 'M', 'N', 'O', 'PW', 'Q', 'Q1', 'Q2', 'R', 'R1', 'RI', 'RU', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];
  if (value && validRoofCodes.includes(value)) return value;

  // Code-based mapping for dakbedekking_code (most reliable)
  // Common Infofolio codes:
  // 10-50 = Hard (pannen, leisteen, bitumen, etc.)
  // 60 = Riet (natural thatch) - maps to K for Woonhuis (no 'R' option)
  // 70 = Kunstriet (artificial thatch)
  if (dakbedekkingCode) {
    const code = parseInt(dakbedekkingCode, 10);
    if (!isNaN(code)) {
      if (code >= 10 && code < 60) return 'H'; // Hard coverings
      if (code === 60) return 'K'; // Riet -> K for Woonhuis (no 'R' option)
      if (code === 70) return 'K'; // Kunstriet
    }
  }

  // Mapping for dakbedekking (roof covering) - most specific
  const bedekkingMappings: Record<string, string> = {
    'pannen': 'H', 'dakpannen': 'H', 'keramische pannen': 'H', 'betonpannen': 'H',
    'leisteen': 'H', 'leien': 'H', 'slate': 'H',
    'bitumen': 'H', 'bitumineus': 'H', 'dakleer': 'H',
    'kunststof': 'H', 'pvc': 'H', 'epdm': 'H',
    'metaal': 'H', 'zink': 'H', 'koper': 'H', 'staal': 'H',
    // Riet maps to K (kunststof riet) for Woonhuis - no 'R' option
    'riet': 'K', 'rieten': 'K', 'stro': 'K', 'thatched': 'K', 'reed': 'K',
    'kunststof riet': 'K', 'synthetisch riet': 'K', 'nep riet': 'K', 'kunstriet': 'K',
    'kunst riet': 'K', 'kunstmatig riet': 'K', 'imitatie riet': 'K',
  };

  // Check dakbedekking first (most accurate)
  if (dakbedekking) {
    const lowerBedekking = dakbedekking.toLowerCase();
    if (bedekkingMappings[lowerBedekking]) {
      return bedekkingMappings[lowerBedekking];
    }
  }

  // Mapping for dakconstructie (roof structure) - fallback inference
  const constructieMappings: Record<string, string> = {
    // Sloped roofs typically have hard covering (tiles)
    'schuin': 'H', 'hellend': 'H', 'zadeldak': 'H', 'mansardedak': 'H',
    'schilddak': 'H', 'lessenaarsdak': 'H', 'puntdak': 'H',
    // Flat roofs typically have bitumen (also hard)
    'plat': 'H', 'platdak': 'H', 'flat': 'H',
  };

  // Check dakconstructie as fallback
  if (dakconstructie) {
    const lowerConstructie = dakconstructie.toLowerCase();
    if (constructieMappings[lowerConstructie]) {
      return constructieMappings[lowerConstructie];
    }
  }

  // Original value mapping (backwards compatibility)
  if (value) {
    const valueMappings: Record<string, string> = {
      'hard': 'H', 'pannen': 'H', 'dakpannen': 'H', 'tiles': 'H',
      'bitumen': 'H', 'plat dak': 'H', 'flat roof': 'H',
      'kunststof riet': 'K', 'synthetic reed': 'K', 'riet': 'K', 'reed': 'K',
    };
    const lowerValue = value.toLowerCase();
    if (valueMappings[lowerValue]) {
      return valueMappings[lowerValue];
    }
  }

  // Return null for unknown values - let user select
  return null;
};
